'use client';

import { useState, useMemo, Suspense } from 'react';
import type { Metadata } from 'next';
import { getAllQuestions, getTopTags, getTotalQuestionCount } from '@/lib/questions';
import { applyFilters, initSearch } from '@/lib/search';
import { SearchBar } from '@/components/ui/SearchBar';
import { FilterPanel } from '@/components/ui/FilterPanel';
import { QuestionCard } from '@/components/ui/QuestionCard';
import { QuestionCardSkeleton } from '@/components/ui/QuestionCardSkeleton';
import { useGameStore } from '@/store/useGameStore';
import { generateBreadcrumbSchema } from '@/lib/seo';
import categoriesData from '@/data/categories.json';
import type { Category, SearchFilters, CategorySlug } from '@/types';

const INITIAL_SHOW = 20;
const LOAD_MORE_COUNT = 20;

const DEFAULT_FILTERS: SearchFilters = {
  query: '',
  categories: [],
  difficulties: [],
  tags: [],
  sortBy: 'difficulty-asc',
  showAnsweredOnly: false,
  showUnansweredOnly: false,
};

const allQuestions = getAllQuestions();
const allCategories = categoriesData as Category[];
const topTags = getTopTags(30);

// Initialize search index eagerly
initSearch(allQuestions);

export default function QuestionsPage() {
  const [filters, setFilters] = useState<SearchFilters>(DEFAULT_FILTERS);
  const [showCount, setShowCount] = useState(INITIAL_SHOW);
  const answeredCount = useGameStore((s) => s.answeredQuestions.length);

  const filtered = useMemo(() => {
    return applyFilters(allQuestions, filters);
  }, [filters]);

  const displayed = filtered.slice(0, showCount);

  const handleQueryChange = (q: string) => {
    setFilters((f) => ({ ...f, query: q }));
    setShowCount(INITIAL_SHOW);
  };

  const handleFiltersChange = (next: SearchFilters) => {
    setFilters(next);
    setShowCount(INITIAL_SHOW);
  };

  const categoryMap = useMemo(() => {
    return Object.fromEntries(allCategories.map((c) => [c.slug, c])) as Record<CategorySlug, Category>;
  }, []);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://dartdosage.fluxpert.com' },
    { name: 'Questions' },
  ]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      {/* Header */}
      <div className="mb-8">
        <h1
          className="font-display font-bold mb-2"
          style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', color: 'var(--color-text-primary)' }}
        >
          Flutter Interview <span className="gradient-text">Questions</span>
        </h1>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          {getTotalQuestionCount()} questions across 15 categories. Study, filter, and track your progress.
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <SearchBar
          value={filters.query}
          onChange={handleQueryChange}
          resultCount={filters.query ? filtered.length : undefined}
        />
      </div>

      <div className="flex gap-6 items-start">
        {/* Sidebar filter — hidden on mobile */}
        <div className="hidden md:block w-64 flex-shrink-0">
          <FilterPanel
            filters={filters}
            onFiltersChange={handleFiltersChange}
            categories={allCategories}
            topTags={topTags}
            totalAnswered={answeredCount}
            totalQuestions={allQuestions.length}
          />
        </div>

        {/* Grid */}
        <div className="flex-1 min-w-0">
          {/* Result count */}
          <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
            {filtered.length === allQuestions.length
              ? `Showing all ${allQuestions.length} questions`
              : `${filtered.length} of ${allQuestions.length} questions`}
          </p>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-4xl mb-4">🔍</p>
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                No questions found
              </h2>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Try a different search term or reset the filters.
              </p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <Suspense fallback={
                  Array.from({ length: 4 }, (_, i) => <QuestionCardSkeleton key={i} />)
                }>
                  {displayed.map((q) => (
                    <QuestionCard
                      key={q.id}
                      question={q}
                      category={categoryMap[q.category]}
                    />
                  ))}
                </Suspense>
              </div>

              {showCount < filtered.length && (
                <div className="text-center mt-8">
                  <button
                    onClick={() => setShowCount((n) => n + LOAD_MORE_COUNT)}
                    className="btn-ghost"
                  >
                    Load More ({filtered.length - showCount} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
